import React, { useCallback, useMemo, useState } from 'react'
import styled from '~/app/components/styled'
import { useInView } from 'react-intersection-observer'
import yaLogo from '~/app/imgs/empty-thumnail-yanolja-h-56.svg?inline'
import { SERVICE_TYPE, STATUS, statusToString } from '~/app/utils/OrderListUtils'
import { MyRoomImage } from '~/app/icons'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { ConvertedOrder } from '~/app/components/order-converter/getCartOrderConverter'
import { useBusTransportationCompanyQuery } from '~/__generated__/app/query/order-details/BusTransportationCompany.graphql'
import { Popup } from '@yanolja-org/ya-com-popup'
import { CategoryType } from '~/__generated__/__types__'
import isBusImageSkip from '~/app/utils/isBusImageSkip'
import { ConfigBadgeWrapper } from '~/app/components/common/Badge/Badge.styles'
import ConfigBadgeGroup from '~/app/components/common/Badge/ConfigBadgeGroup'
import { getYaNative } from '@yanolja-org/ya-native'
import OrderCardItemButtons from '~/app/components/order-list/common/OrderCardItemButtons'
import PendingReservationNotice from '~/app/components/order-list/common/PendingReservationNotice'
import ExceptionalCancelPolicyText from '~/app/components/common/ExceptionalCancelPolicy/ExceptionalCancelPolicyText'
import LeisureCancelPolicy from '~/app/components/common/Leisure/LeisureCancelPolicy'
import useCancelCommission from '~/app/components/order-list/hooks/useCancelCommission'
import { isLeisureCategoryType } from '~/app/components/order-converter/converter/LeisureOrderConverter'
import { isNative } from '~/app/utils/typeguards'
import LeisureCancelRequestedNotice from '~/app/components/order-list/common/LeisureCancelRequestedNotice'
import CancelPolicyText, {
  CancelPolicyFetchFailText,
} from '~/app/components/common/CancelPolicy/CancelPolicyText'
import InfoLineToSpan from '~/app/components/common/InfoLineToSpan'
import { MemberClassSpecialPriceBadge } from '~/app/components-nds/common/member-class'
import { Icon_arrowforward_fill } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'
import LeisureOptionTitle from './LeisureOptionTitle'
import { Info, SubTitle, expired } from './styled'
import FetchRetryButton from '~/app/components/common/FetchRetry/FetchRetryButton'
import FetchFailText from '~/app/components/common/FetchRetry/FetchFailText'

/**
 * CardItem component
 * author: soojin.oh
 */

// type definitions
export type Props = ConvertedOrder & {
  id: string
  className?: string
  hasDetailButton?: boolean
  hideServiceType?: boolean
  onImpressed?: (id: string) => void
  onClick?: (id: string, url: string, deeplink?: string) => void
  onButtonClick?: (id: string, data?: any) => void
  onButtonImpressed?: (id: string, data?: any) => void
  isDisabledLazyLoadImage?: boolean
  onCancelRefreshButtonClick?: () => void
  showCancelPolicyRetry?: boolean
  fromDetail?: boolean
  isSharePage?: boolean
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'style'>

export const CategoryTypeText = {
  [CategoryType.DomesticReservation]: '숙소',
  [CategoryType.DomesticTrain]: '기차',
  [CategoryType.DomesticBus]: '고속버스',
  [CategoryType.DomesticLeisureTicket]: '레저',
  [CategoryType.DomesticLeisureRentacar]: '렌터카',
  [CategoryType.DomesticLeisureGiftcard]: '모바일교환권',
}

// styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  padding: 1.6rem 2rem;

  &.clickable {
    cursor: pointer;
  }
`
const TopLine = styled.div`
  color: ${({ theme }) => theme.color.gray600};
  ${Typography('caption').regular}
  text-align: left;

  &.cancel-completed,
  &.completed,
  &.cancel-requested {
    color: ${({ theme }) => theme.color.gray400};
  }

  &:empty {
    margin-bottom: 0;
  }
`
const Status = styled.div`
  display: inline-block;
  border-radius: 0.4rem;
  background: ${({ theme }) => theme.color.gray900};
  padding: 0.3rem 0.6rem 0;
  ${Typography('badge').bold};
  color: ${({ theme }) => theme.color.white};
  height: 1.8rem;

  .cancel-completed &,
  .completed & {
    background: ${({ theme }) => theme.color.gray400};
    color: ${({ theme }) => theme.color.white};
  }
`
const Content = styled.div`
  display: flex;
  flex: 1 0 auto;
`
const ImageContainer = styled.div`
  position: relative;
  margin-right: 0.8rem;
`
const ImageMask = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  .cancel-completed &,
  .completed &,
  .cancel-requested & {
    background: ${({ theme: { color } }) => color.white_op40};
  }
`
const Image = styled.img`
  position: relative;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  background-color: ${({ theme: { color } }) => color.gray50};
  object-fit: cover;
  max-width: none;
`
const LazyImage = styled(LazyLoadImage)`
  position: relative;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  background-color: ${({ theme: { color } }) => color.gray50};
  object-fit: cover;
  max-width: none;
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.gray900};
  ${Typography('subtitle3').bold}
  margin-bottom: 0.8rem;
  word-break: break-word;
  ${({ theme }) => expired(theme)};
`
const RatePlanTitle = styled.div`
  color: ${({ theme }) => theme.color.gray900};
  ${Typography('body1').regular}
  margin-bottom: 0.8rem;
  word-break: break-word;
  ${({ theme }) => expired(theme)};
`
/**
 * html2canvas 캡처 시 line-height 와 폰트 baseline 계산 차이로 텍스트가
 * 박스 안에서 위쪽으로 치우쳐 보이는 문제(예: 예약확인서 캡처)를 회피하기 위해,
 * 캡처 컨테이너 측에서 이 className 을 selector 로 잡아 line-height 를 보정한다.
 */
export const ORDER_CARD_OPTIONS_CLASS = 'order-card-options'

const Options = styled.div`
  color: ${({ theme }) => theme.color.gray600};
  ${Typography('body2.multiline').regular}
  margin-bottom: 0.8rem;
  word-break: keep-all;
  ${({ theme }) => expired(theme)};
  padding: 0.6rem 1rem;
  border-radius: 0.6rem;
  background: ${({ theme }) => theme.color.gray50};
`
const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
const CardItemWrapper = styled.div`
  width: 100%;
`
const SVGFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  margin-right: 0.8rem;
  background: ${({ theme: { color } }) => color.gray50};
`
export const DefaultBusLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  margin-right: 0.8rem;
  padding: 0 0.5rem;
  background: ${({ theme: { color } }) => color.gray50};
  ${Typography('body2').regular}
  text-align: center;
  color: ${({ theme: { color } }) => color.gray600};
`
const CategoryOrderIdText = styled.div`
  ${Typography('body2').regular}
  color: ${({ theme: { color } }) => color.gray600};
  margin-bottom: 0.8rem;
  &.cancel-completed,
  &.completed {
    color: ${({ theme }) => theme.color.gray400};
  }
`
const ServiceTypeText = styled.div`
  ${Typography('body2').regular}
  color: ${({ theme: { color } }) => color.gray600};
  margin-bottom: 1rem;
  &.cancel-completed,
  &.completed {
    color: ${({ theme }) => theme.color.gray400};
  }
`
const ButtonContainer = styled.div`
  margin-top: 1.2rem;
`
const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const ArrowForwardLineIcon = styled(Icon_arrowforward_fill)`
  margin-left: 0.8rem;
  flex: 0 0 auto;
`
const ExceptionalCancelPolicyTextWrapper = styled.div`
  display: flex;
  & > div {
    flex: 1;
  }
`
const LeisureCancelPolicyWrapper = styled.div`
  margin-top: 1.6rem;
  & > span {
    width: 100%;
  }
`
const CancelPolicyTextWrapper = styled.div`
  margin-top: 1.6rem;
`
const CancelPolicyFetchFailWrapper = styled.div`
  margin-top: 0.9rem;
  display: grid;
  grid-auto-flow: row;
  gap: 1.2rem;

  button {
    width: 7.4rem;
  }
`
const FetchFailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

// CardItem component
export default function OrderCardItem({
  id,
  categoryOrderId,
  serviceType,
  categoryType,
  imageSrc,
  title,
  status,
  subTitle,
  ratePlanTitle,
  info = [],
  options,
  secondarySubTitle,
  secondaryInfo,
  className = '',
  hasDetailButton,
  hideServiceType = false,
  style,
  isInfiniteCouponRoom,
  onClick,
  onImpressed,
  pdpUrl = '',
  deeplink,
  busTransportationCompanyCode,
  buttons,
  onButtonClick,
  onButtonImpressed,
  isDisabledLazyLoadImage,
  badges,
  onCancelRefreshButtonClick,
  showCancelPolicyRetry,
  cancelPolicySummary,
  hasPenalty,
  isPendingReservation,
  fromDetail = false,
  isExceptionalCancelPolicy,
  refundInfo,
  isSharePage,
  isShowLeisureCancelRequestNotice,
  propertyCategory,
  memberClassBadge,
  optionTitle,
  cancellationFeePlans,
  isAPIFailed,
}: Props) {
  const [isAvailableMoveToBusProduct, setIsAvailableMoveToBusProduct] = useState<boolean>(false)
  const toInformation = useCallback(
    info =>
      info?.map((line, index) => (
        <Info key={index}>
          <InfoLineToSpan line={line} />
        </Info>
      )),
    []
  )

  const hasBadges = useMemo(
    () => status || badges || isInfiniteCouponRoom,
    [status, badges, isInfiniteCouponRoom]
  )

  const information = useMemo(() => toInformation(info), [info])

  const secondaryInformation = useMemo(() => toInformation(secondaryInfo), [secondaryInfo])

  const [imageLoadFail, setImageLoadFail] = useState(false)

  const LogoImage = useMemo(() => {
    if (typeof imageSrc === 'string') {
      const src = imageLoadFail ? yaLogo : (imageSrc as string)
      return isDisabledLazyLoadImage ? (
        <Image alt={'상품 사진'} src={src} onError={() => setImageLoadFail(true)} />
      ) : (
        <LazyImage
          alt={'상품 사진'}
          src={src}
          placeholderSrc={yaLogo}
          onError={() => setImageLoadFail(true)}
        />
      )
    } else {
      return <SVGFrame>{imageSrc}</SVGFrame>
    }
  }, [imageSrc, imageLoadFail, isDisabledLazyLoadImage])

  const { data: busTransportationCompanyData } = useBusTransportationCompanyQuery({
    variables: {
      code: busTransportationCompanyCode!,
    },
    skip: isBusImageSkip({ busTransportationCompanyCode, imageSrc }),
  })

  const { ref, inView } = useInView({ triggerOnce: true })
  if (inView) {
    onImpressed?.(id)
  }

  const onClickItem = () => {
    const yaNative = getYaNative()
    if (categoryType === CategoryType.DomesticBus && !isNative(yaNative) && !isSharePage) {
      setIsAvailableMoveToBusProduct(true)
    } else {
      onClick?.(id, pdpUrl, deeplink)
    }
  }

  const closeAvailableMoveToBusProduct = () => {
    setIsAvailableMoveToBusProduct(false)
  }

  /**
   * props 로 categoryType 이 넘어오지 않으면 예약번호 비노출처리
   * -> 공유하기 페이지에서 노출하지 않기 위함
   */
  const categoryTypeText = useMemo<string>(() => {
    return CategoryTypeText[categoryType || ''] || ''
  }, [categoryType])

  const { cancelSummary } = useCancelCommission(cancelPolicySummary)

  const isShowExceptionalCancelPolicy = useMemo(() => {
    return serviceType === SERVICE_TYPE.ACCOMMODATION
      ? isExceptionalCancelPolicy && !cancelSummary?.cancelable
      : false
  }, [cancelSummary, isExceptionalCancelPolicy])

  const isShowCancelPolicy = useMemo(() => {
    return !!cancelSummary && !hasPenalty && cancelSummary?.nonCancellationReason !== 'UNAVAILABLE'
  }, [cancelSummary, hasPenalty])

  return (
    <>
      <Container ref={ref} className={className} style={style} onClick={onClickItem}>
        {!!categoryTypeText && (
          <CategoryOrderIdText className={statusToString(status)}>
            {categoryTypeText} 예약번호 {categoryOrderId}
          </CategoryOrderIdText>
        )}
        {!categoryTypeText && hideServiceType === false && (
          <ServiceTypeText className={statusToString(status)}>{serviceType}</ServiceTypeText>
        )}
        <TopLine className={statusToString(status)}>
          {hasBadges && (
            <ConfigBadgeWrapper>
              {status && <Status data-testid={'orderDetail-status'}>{status}</Status>}
              <ConfigBadgeGroup badges={badges} />
              {isInfiniteCouponRoom && <MyRoomImage />}
            </ConfigBadgeWrapper>
          )}
          <Title>{title}</Title>
        </TopLine>

        <Content className={statusToString(status)}>
          <ImageContainer className={statusToString(status)}>
            {serviceType === SERVICE_TYPE.BUS ? (
              imageSrc ? (
                LogoImage
              ) : (
                <DefaultBusLogo>
                  {busTransportationCompanyData?.busTransportationCompany?.name ?? ''}
                </DefaultBusLogo>
              )
            ) : (
              LogoImage
            )}
            <ImageMask style={{ visibility: imageLoadFail ? 'hidden' : 'visible' }} />
          </ImageContainer>
          {isAPIFailed ? (
            <FetchFailWrapper>
              <FetchFailText />
              <FetchRetryButton onClick={onCancelRefreshButtonClick} />
            </FetchFailWrapper>
          ) : (
            <ContentWrapper>
              <ContentContainer>
                <CardItemWrapper>
                  {subTitle && <SubTitle>{subTitle}</SubTitle>}
                  {ratePlanTitle && <RatePlanTitle>{ratePlanTitle}</RatePlanTitle>}
                  {optionTitle && <LeisureOptionTitle data={optionTitle} />}
                  {information}
                  {secondarySubTitle && (
                    <div style={{ marginTop: 8 }}>
                      <SubTitle>{secondarySubTitle}</SubTitle>
                      {secondaryInformation}
                    </div>
                  )}
                  {options && <Options className={ORDER_CARD_OPTIONS_CLASS}>{options}</Options>}

                  {memberClassBadge && (
                    <MemberClassSpecialPriceBadge memberClass={memberClassBadge} />
                  )}

                  {isLeisureCategoryType(categoryType) && !fromDetail ? (
                    <LeisureCancelPolicyWrapper>
                      <LeisureCancelPolicy
                        cancellationFeePlans={cancellationFeePlans ?? []}
                        refundInfo={refundInfo}
                        isAvailableUse={status === STATUS.READY}
                      />
                    </LeisureCancelPolicyWrapper>
                  ) : cancelSummary ? (
                    isShowCancelPolicy && (
                      <CancelPolicyTextWrapper>
                        <CancelPolicyText
                          cancelPolicySummary={cancelSummary}
                          propertyCategory={propertyCategory}
                          isPendingReservation={isPendingReservation}
                        />
                      </CancelPolicyTextWrapper>
                    )
                  ) : (
                    showCancelPolicyRetry && (
                      <CancelPolicyFetchFailWrapper>
                        <CancelPolicyFetchFailText />
                        <FetchRetryButton onClick={onCancelRefreshButtonClick} />
                      </CancelPolicyFetchFailWrapper>
                    )
                  )}
                  {isShowExceptionalCancelPolicy && (
                    <ExceptionalCancelPolicyTextWrapper>
                      <ExceptionalCancelPolicyText isExceptionalCancelPolicy isCancelFree={false} />
                    </ExceptionalCancelPolicyTextWrapper>
                  )}
                </CardItemWrapper>
                {hasDetailButton && <ArrowForwardLineIcon size={1.4} unit="rem" />}
              </ContentContainer>
              {!!buttons?.length && (
                <ButtonContainer>
                  <OrderCardItemButtons
                    buttons={buttons}
                    onButtonClick={(id, data) => onButtonClick?.(id, data)}
                    onButtonImpressed={(id, data) => onButtonImpressed?.(id, data)}
                  />
                </ButtonContainer>
              )}
            </ContentWrapper>
          )}
        </Content>

        {isPendingReservation && !fromDetail && <PendingReservationNotice />}
        {isShowLeisureCancelRequestNotice && <LeisureCancelRequestedNotice />}
      </Container>
      <Popup
        popupId={'BUS_PRODUCT_MOVE_POPUP'}
        buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
        isOpened={isAvailableMoveToBusProduct}
        title={'고속버스 상품은 앱에서만 확인할 수 있어요'}
        onButtonClick={closeAvailableMoveToBusProduct}
        onBackgroundClick={closeAvailableMoveToBusProduct}
        onPopupClose={closeAvailableMoveToBusProduct}
      />
    </>
  )
}
